/**
 * Pedidos de Matrícula — las sucursales piden a la central los documentos
 * (matrícula, placa, copias…) de los chasis que vendieron.
 *
 * Tres vistas:
 *   Pedir         chasis vendidos por la sucursal que aún no tiene sus
 *                 documentos; un pedido por chasis con los tipos elegidos.
 *   Mis pedidos   seguimiento de lo pedido (y cancelar lo no aceptado).
 *   Bandeja       solo con el flag `modificar` de pedidomatriculas: la
 *                 central acepta y rechaza (con razón). Las solicitudes DGII
 *                 o de proveedor NO se arman aquí: se hacen en Solicitudes
 *                 DGII/Proveedor y el pedido las sigue solo.
 *
 * Después de aceptar, el estado lo derivan los otros procesos (solicitud
 * DGII, recepción, envío, entrega): la pantalla no marca nada a mano. La
 * central es varinicial.sucursalmatricula (Configuración → Parámetros); sin
 * ella la pantalla solo muestra el aviso. Ver pedidomatriculabackend.py.
 */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid } from "@mui/x-data-grid";
import RefreshIcon from "@mui/icons-material/Refresh";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import AddShoppingCartRoundedIcon from "@mui/icons-material/AddShoppingCartRounded";
import ListAltRoundedIcon from "@mui/icons-material/ListAltRounded";
import InboxRoundedIcon from "@mui/icons-material/InboxRounded";
import { useGlobalMDAlert, useMaterialUIController } from "context";
import { FP, PageHeader, fpInputSx, fpDataGridSx } from "components/financepro";
import { useApi, useSucursales } from "lib/api";
import { puedeAccionPrograma } from "lib/seguridad/permisosPorModulo";

export const PROGRAMA_PEDIDOS = "pedidomatriculas";

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});

const ESTADOS = {
  pedido: { label: "Pedido", bg: "#FFF3E0", fg: "#E65100" },
  aceptado: { label: "Aceptado", bg: "#E3F2FD", fg: "#1565C0" },
  "en gestion": { label: "En gestión DGII", bg: "#EDE7F6", fg: "#5E35B1" },
  "recibido central": { label: "En la central", bg: "#E0F2F1", fg: "#00695C" },
  enviado: { label: "Enviado", bg: "#E1F5FE", fg: "#0277BD" },
  "recibido sucursal": { label: "En la sucursal", bg: "#E8F5E9", fg: "#2E7D32" },
  entregado: { label: "Entregado", bg: "#ECEFF1", fg: "#455A64" },
  rechazado: { label: "Rechazado", bg: "#FFEBEE", fg: "#C62828" },
  cancelado: { label: "Cancelado", bg: "#F5F5F5", fg: "#757575" },
};

export const chipEstadoPedido = (estado) => {
  const e = ESTADOS[estado] || { label: estado, bg: "#ECEFF1", fg: "#455A64" };
  return <Chip size="small" label={e.label} sx={{ bgcolor: e.bg, color: e.fg, fontWeight: 700 }} />;
};

const FILTROS_ESTADO = [
  { value: "vivos", label: "En curso" },
  { value: "", label: "Todos" },
  ...Object.entries(ESTADOS).map(([value, e]) => ({ value, label: e.label })),
];

const haceUnAnio = () => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 1);
  return d.toISOString().slice(0, 10);
};

const textoDocumentos = (row) =>
  (row.documentos || [])
    .map((t) => {
      const u = row.ubicaciones?.[t];
      return u ? `${t} (${u})` : t;
    })
    .join(", ");

/** Filtro local por chasis: parte del número, sin distinguir mayúsculas ni espacios. */
const normalizarChasis = (v) =>
  String(v || "")
    .toUpperCase()
    .replace(/\s+/g, "");
const filtrarChasis = (rows, texto) => {
  const buscado = normalizarChasis(texto);
  return buscado ? rows.filter((r) => normalizarChasis(r.chasis).includes(buscado)) : rows;
};

function FiltroChasis({ value, onChange }) {
  return (
    <TextField
      fullWidth
      label="Chasis"
      placeholder="Buscar…"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      sx={fpInputSx}
    />
  );
}

const VISTAS = [
  { id: "pedir", title: "Pedir", icon: AddShoppingCartRoundedIcon },
  { id: "mis", title: "Mis pedidos", icon: ListAltRoundedIcon },
  { id: "bandeja", title: "Bandeja de la central", icon: InboxRoundedIcon, gestion: true },
];

// ---------------------------------------------------------------------------
// Pedir
// ---------------------------------------------------------------------------

function Pedir({ tipos, onCreado }) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  const [desde, setDesde] = useState(haceUnAnio());
  const [buscar, setBuscar] = useState("");
  const [rows, setRows] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [seleccion, setSeleccion] = useState([]);
  const [elegidos, setElegidos] = useState(() =>
    ["matricula", "placa"].filter((t) => tipos.includes(t))
  );
  const [comentario, setComentario] = useState("");
  const [confirmar, setConfirmar] = useState(false);
  const [enviando, setEnviando] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const { ok, data } = await post("/api/pedidomatricula_candidatos", { desde });
      if (ok && Array.isArray(data.candidatos)) {
        setRows(data.candidatos);
        setSeleccion([]);
      } else {
        showAlert({ message: data.Error || "No se pudieron cargar los chasis", color: "error" });
      }
    } finally {
      setCargando(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post, desde]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  // Por chasis: los tipos elegidos que todavía le faltan a la sucursal.
  const items = useMemo(
    () =>
      rows
        .filter((r) => seleccion.includes(r.chasis))
        .map((r) => ({ ...r, tipos: elegidos.filter((t) => r.pendientes.includes(t)) })),
    [rows, seleccion, elegidos]
  );
  const vacios = items.filter((i) => i.tipos.length === 0);
  const sinCobro = items.filter((i) => !i.concobro);

  const enviar = async () => {
    setEnviando(true);
    try {
      const { ok, data } = await post("/api/pedidomatricula_crear", {
        items: items
          .filter((i) => i.tipos.length)
          .map((i) => ({ chasis: i.chasis, tipos: i.tipos })),
        comentario,
      });
      if (ok) {
        setConfirmar(false);
        setComentario("");
        showAlert({
          message: data.mensajeok || "Pedido enviado",
          color: data.sincobro?.length ? "warning" : "success",
        });
        cargar();
        onCreado();
      } else {
        showAlert({ message: data.Error || "No se pudo crear el pedido", color: "error" });
      }
    } finally {
      setEnviando(false);
    }
  };

  const columnas = [
    { field: "chasis", headerName: "Chasis", flex: 1, minWidth: 160 },
    {
      field: "vehiculo",
      headerName: "Vehículo",
      flex: 1,
      minWidth: 140,
      valueGetter: (_v, row) => [row.marca, row.modelo, row.colore].filter(Boolean).join(" "),
    },
    { field: "cliente", headerName: "Cliente", flex: 1.2, minWidth: 160 },
    { field: "sucursal", headerName: "Sucursal", flex: 0.8, minWidth: 110 },
    { field: "fechaventa", headerName: "Venta", width: 100 },
    {
      field: "recibida",
      headerName: "Matrícula",
      width: 130,
      renderCell: (p) =>
        p.value ? (
          <Chip
            size="small"
            label="Ya recibida"
            sx={{ bgcolor: "#E0F2F1", color: "#00695C", fontWeight: 700 }}
          />
        ) : (
          <Chip
            size="small"
            label="Sin tramitar"
            sx={{ bgcolor: "#ECEFF1", color: "#455A64", fontWeight: 700 }}
          />
        ),
    },
    {
      field: "concobro",
      headerName: "Cobro",
      width: 110,
      renderCell: (p) =>
        p.value ? (
          "Cobrada"
        ) : (
          <Tooltip title="No tiene cobro de matrícula: se puede pedir igual">
            <Chip
              size="small"
              label="Sin cobro"
              sx={{ bgcolor: "#FFF3E0", color: "#E65100", fontWeight: 700 }}
            />
          </Tooltip>
        ),
    },
    {
      field: "pendientes",
      headerName: "Le faltan",
      flex: 1.2,
      minWidth: 170,
      valueGetter: (v) => (v || []).join(", "),
    },
  ];

  return (
    <>
      <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
        <Grid item xs={12} sm={4} md={2}>
          <TextField
            fullWidth
            type="date"
            label="Vendidos desde"
            value={desde}
            onChange={(e) => setDesde(e.target.value)}
            InputLabelProps={{ shrink: true }}
            helperText="Vacío = todas las ventas"
            sx={fpInputSx}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={2}>
          <FiltroChasis value={buscar} onChange={setBuscar} />
        </Grid>
        <Grid item xs={12} sm={4} md={4}>
          <Autocomplete
            multiple
            options={tipos}
            value={elegidos}
            onChange={(_, v) => setElegidos(v)}
            renderInput={(params) => (
              <TextField {...params} label="Documentos a pedir" sx={fpInputSx} />
            )}
          />
        </Grid>
        <Grid item xs={12} md={4} sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
          <Button
            startIcon={cargando ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
            onClick={cargar}
            disabled={cargando}
            sx={btnOutline(FP.color.navy)}
          >
            Actualizar
          </Button>
          <Button
            startIcon={<SendRoundedIcon />}
            disabled={!seleccion.length || !elegidos.length || vacios.length === items.length}
            onClick={() => setConfirmar(true)}
            sx={btnSolid(FP.color.navy, FP.color.navySoft)}
          >
            Pedir {seleccion.length ? `(${seleccion.length})` : ""}
          </Button>
        </Grid>
      </Grid>

      <DataGrid
        autoHeight
        rows={filtrarChasis(rows, buscar)}
        columns={columnas}
        getRowId={(r) => r.chasis}
        loading={cargando}
        checkboxSelection
        // Lo marcado se conserva al cambiar la búsqueda: se puede buscar un
        // chasis, marcarlo, buscar el siguiente y pedirlos juntos.
        keepNonExistentRowsSelected
        rowSelectionModel={seleccion}
        onRowSelectionModelChange={setSeleccion}
        disableRowSelectionOnClick
        pageSizeOptions={[25, 50, 100]}
        initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        localeText={{ noRowsLabel: "No hay chasis pendientes de documentos" }}
        sx={fpDataGridSx}
      />

      <Dialog open={confirmar} onClose={() => setConfirmar(false)} fullWidth maxWidth="sm">
        <DialogTitle>Pedir a la central</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            {items
              .filter((i) => i.tipos.length)
              .map((i) => (
                <Typography key={i.chasis} sx={{ fontSize: "0.85rem" }}>
                  <b>{i.chasis}</b> {i.cliente && `· ${i.cliente}`} — {i.tipos.join(", ")}
                </Typography>
              ))}
            {vacios.length > 0 && (
              <Alert severity="info">
                Se omiten {vacios.length} chasis que ya tienen los documentos elegidos:{" "}
                {vacios.map((i) => i.chasis).join(", ")}
              </Alert>
            )}
            {sinCobro.length > 0 && (
              <Alert severity="warning">
                Sin cobro de matrícula: {sinCobro.map((i) => i.chasis).join(", ")}. El pedido se
                envía igual.
              </Alert>
            )}
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Comentario para la central (opcional)"
              value={comentario}
              onChange={(e) => setComentario(e.target.value)}
              inputProps={{ maxLength: 300 }}
              sx={fpInputSx}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmar(false)} sx={btnOutline(FP.color.navy)}>
            Cerrar
          </Button>
          <Button
            onClick={enviar}
            disabled={enviando}
            startIcon={
              enviando ? <CircularProgress size={16} color="inherit" /> : <SendRoundedIcon />
            }
            sx={btnSolid(FP.color.navy, FP.color.navySoft)}
          >
            Enviar pedido
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// ---------------------------------------------------------------------------
// Listado (Mis pedidos y Bandeja)
// ---------------------------------------------------------------------------

function columnasPedido({ conSucursal }) {
  return [
    { field: "id", headerName: "#", width: 70 },
    { field: "fecha", headerName: "Pedido", width: 100 },
    {
      field: "dias",
      headerName: "Días",
      width: 70,
      type: "number",
    },
    ...(conSucursal
      ? [{ field: "sucursal", headerName: "Sucursal", flex: 0.8, minWidth: 110 }]
      : []),
    { field: "chasis", headerName: "Chasis", flex: 1, minWidth: 160 },
    { field: "cliente", headerName: "Cliente", flex: 1.1, minWidth: 150 },
    {
      field: "documentos",
      headerName: "Documentos",
      flex: 1.4,
      minWidth: 200,
      valueGetter: (_v, row) => textoDocumentos(row),
    },
    {
      field: "estado",
      headerName: "Estado",
      width: 150,
      renderCell: (p) => {
        const detalle = [
          p.row.observada && `Observada en DGII: ${p.row.motivoobservacion || "sin motivo"}`,
          p.row.razonrechazo && `Rechazo: ${p.row.razonrechazo}`,
          p.row.razoncancelacion && `Cancelación: ${p.row.razoncancelacion}`,
          p.row.comentario && `Comentario: ${p.row.comentario}`,
        ].filter(Boolean);
        const chip = chipEstadoPedido(p.value);
        return detalle.length ? (
          <Tooltip title={detalle.join(" · ")}>
            <span>{chip}</span>
          </Tooltip>
        ) : (
          chip
        );
      },
    },
    {
      field: "solicitud_id",
      headerName: "Solicitud",
      width: 120,
      renderCell: (p) =>
        p.value ? (
          <Typography sx={{ fontSize: "0.8rem", color: p.row.observada ? "#C62828" : undefined }}>
            #{p.value}
            {p.row.numerodocumento ? ` · ${p.row.numerodocumento}` : ""}
            {p.row.observada ? " (obs.)" : ""}
          </Typography>
        ) : (
          ""
        ),
    },
    {
      field: "sincobro",
      headerName: "Cobro",
      width: 95,
      renderCell: (p) =>
        p.value ? (
          <Chip
            size="small"
            label="Sin cobro"
            sx={{ bgcolor: "#FFF3E0", color: "#E65100", fontWeight: 700 }}
          />
        ) : (
          ""
        ),
    },
    { field: "user", headerName: "Pidió", width: 110 },
  ];
}

function FiltroEstado({ value, onChange }) {
  return (
    <TextField
      select
      fullWidth
      label="Estado"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      sx={fpInputSx}
    >
      {FILTROS_ESTADO.map((o) => (
        <MenuItem key={o.value || "todos"} value={o.value}>
          {o.label}
        </MenuItem>
      ))}
    </TextField>
  );
}

function MisPedidos({ recargar }) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  const [estado, setEstado] = useState("vivos");
  const [buscar, setBuscar] = useState("");
  const [rows, setRows] = useState([]);
  const [todas, setTodas] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [cancelar, setCancelar] = useState(null);
  const [razon, setRazon] = useState("");
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const { ok, data } = await post("/api/pedidomatricula_listar", { estado });
      if (ok && Array.isArray(data.pedidos)) {
        setRows(data.pedidos);
        setTodas(!!data.todas);
      } else {
        showAlert({ message: data.Error || "No se pudieron cargar los pedidos", color: "error" });
      }
    } finally {
      setCargando(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post, estado, recargar]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const confirmarCancelar = async () => {
    setGuardando(true);
    try {
      const { ok, data } = await post("/api/pedidomatricula_cancelar", { id: cancelar.id, razon });
      if (ok) {
        setCancelar(null);
        showAlert({ message: "Pedido cancelado", color: "success" });
        cargar();
      } else {
        showAlert({ message: data.Error || "No se pudo cancelar", color: "error" });
      }
    } finally {
      setGuardando(false);
    }
  };

  const columnas = [
    ...columnasPedido({ conSucursal: todas }),
    {
      field: "acciones",
      headerName: "",
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (p) =>
        p.row.estado === "pedido" ? (
          <Button
            size="small"
            onClick={() => {
              setRazon("");
              setCancelar(p.row);
            }}
            sx={{ ...btnBase, color: "#C62828" }}
          >
            Cancelar
          </Button>
        ) : null,
    },
  ];

  return (
    <>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={3} md={2}>
          <FiltroEstado value={estado} onChange={setEstado} />
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <FiltroChasis value={buscar} onChange={setBuscar} />
        </Grid>
        <Grid item xs={12} sm={5} md={7} sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            startIcon={cargando ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
            onClick={cargar}
            disabled={cargando}
            sx={btnOutline(FP.color.navy)}
          >
            Actualizar
          </Button>
        </Grid>
      </Grid>
      <DataGrid
        autoHeight
        rows={filtrarChasis(rows, buscar)}
        columns={columnas}
        loading={cargando}
        disableRowSelectionOnClick
        pageSizeOptions={[25, 50, 100]}
        initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        localeText={{ noRowsLabel: "No hay pedidos" }}
        sx={fpDataGridSx}
      />
      <Dialog open={!!cancelar} onClose={() => setCancelar(null)} fullWidth maxWidth="xs">
        <DialogTitle>Cancelar pedido #{cancelar?.id}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.85rem", mb: 2 }}>
            Chasis {cancelar?.chasis}. La central todavía no lo ha visto.
          </Typography>
          <TextField
            fullWidth
            label="Razón (opcional)"
            value={razon}
            onChange={(e) => setRazon(e.target.value)}
            inputProps={{ maxLength: 300 }}
            sx={fpInputSx}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelar(null)} sx={btnOutline(FP.color.navy)}>
            Cerrar
          </Button>
          <Button
            onClick={confirmarCancelar}
            disabled={guardando}
            sx={btnSolid("#C62828", "#B71C1C")}
          >
            Cancelar pedido
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function Bandeja({ onCambio }) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  const sucursales = useSucursales({ todas: true });
  const [estado, setEstado] = useState("pedido");
  const [sucursal, setSucursal] = useState(null);
  const [buscar, setBuscar] = useState("");
  const [rows, setRows] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [seleccion, setSeleccion] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [rechazar, setRechazar] = useState(false);
  const [razon, setRazon] = useState("");

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const { ok, data } = await post("/api/pedidomatricula_bandeja", {
        estado,
        sucursal: sucursal ? sucursal.label : "",
      });
      if (ok && Array.isArray(data.pedidos)) {
        setRows(data.pedidos);
        setSeleccion([]);
      } else {
        showAlert({ message: data.Error || "No se pudo cargar la bandeja", color: "error" });
      }
    } finally {
      setCargando(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post, estado, sucursal]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const elegidos = rows.filter((r) => seleccion.includes(r.id));
  const todosEn = (...estados) =>
    elegidos.length > 0 && elegidos.every((r) => estados.includes(r.estado));

  const accion = async (ruta, cuerpo, alTerminar) => {
    setGuardando(true);
    try {
      const { ok, data } = await post(ruta, { ids: seleccion, ...cuerpo });
      if (ok) {
        showAlert({ message: data.mensajeok || "Listo", color: "success" });
        if (alTerminar) alTerminar();
        cargar();
        onCambio();
      } else {
        showAlert({ message: data.Error || "No se pudo completar", color: "error" });
      }
    } finally {
      setGuardando(false);
    }
  };

  return (
    <>
      <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
        <Grid item xs={6} sm={3} md={2}>
          <FiltroEstado value={estado} onChange={setEstado} />
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <Autocomplete
            options={sucursales}
            value={sucursal}
            onChange={(_, v) => setSucursal(v)}
            getOptionLabel={(o) => o.label || ""}
            isOptionEqualToValue={(o, v) => o.label === v.label}
            renderInput={(params) => (
              <TextField {...params} label="Sucursal (todas)" sx={fpInputSx} />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={5} md={2}>
          <FiltroChasis value={buscar} onChange={setBuscar} />
        </Grid>
        <Grid
          item
          xs={12}
          sm={12}
          md={5}
          sx={{ display: "flex", gap: 1, justifyContent: "flex-end", flexWrap: "wrap" }}
        >
          <Button
            startIcon={cargando ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
            onClick={cargar}
            disabled={cargando}
            sx={btnOutline(FP.color.navy)}
          >
            Actualizar
          </Button>
          <Button
            disabled={guardando || !todosEn("pedido")}
            onClick={() => accion("/api/pedidomatricula_aceptar", {})}
            sx={btnSolid("#2E7D32", "#1B5E20")}
          >
            Aceptar
          </Button>
          <Button
            disabled={guardando || !todosEn("pedido", "aceptado")}
            onClick={() => {
              setRazon("");
              setRechazar(true);
            }}
            sx={btnOutline("#C62828")}
          >
            Rechazar
          </Button>
        </Grid>
      </Grid>
      <Typography sx={{ fontSize: "0.78rem", color: FP.color.textMuted, mb: 1 }}>
        Al aceptar, un chasis cuya matrícula ya está recibida pasa directo a «En la central» (no va
        a la DGII). Los demás se tramitan en Solicitudes DGII/Proveedor: al agregar el chasis a una
        solicitud, el pedido pasa solo a «En gestión». El envío a la sucursal se hace desde
        Inventario de Documentos → Crear envío.
      </Typography>
      <DataGrid
        autoHeight
        rows={filtrarChasis(rows, buscar)}
        columns={columnasPedido({ conSucursal: true })}
        loading={cargando}
        checkboxSelection
        keepNonExistentRowsSelected
        rowSelectionModel={seleccion}
        onRowSelectionModelChange={setSeleccion}
        disableRowSelectionOnClick
        pageSizeOptions={[25, 50, 100]}
        initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        localeText={{ noRowsLabel: "No hay pedidos" }}
        sx={fpDataGridSx}
      />

      <Dialog open={rechazar} onClose={() => setRechazar(false)} fullWidth maxWidth="xs">
        <DialogTitle>Rechazar {seleccion.length} pedido(s)</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.85rem", mb: 2 }}>
            La sucursal verá la razón y podrá volver a pedir el chasis.
          </Typography>
          <TextField
            fullWidth
            autoFocus
            label="Razón del rechazo"
            value={razon}
            onChange={(e) => setRazon(e.target.value)}
            inputProps={{ maxLength: 300 }}
            sx={fpInputSx}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRechazar(false)} sx={btnOutline(FP.color.navy)}>
            Cerrar
          </Button>
          <Button
            disabled={guardando || !razon.trim()}
            onClick={() =>
              accion("/api/pedidomatricula_rechazar", { razon }, () => setRechazar(false))
            }
            sx={btnSolid("#C62828", "#B71C1C")}
          >
            Rechazar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// ---------------------------------------------------------------------------
// Pantalla
// ---------------------------------------------------------------------------

export default function PedidosMatricula() {
  const { post } = useApi();
  const [{ roleDetalles }] = useMaterialUIController();
  const puedeGestionar = puedeAccionPrograma(roleDetalles, PROGRAMA_PEDIDOS, "modificar");
  const location = useLocation();
  const inicial = new URLSearchParams(location.search).get("vista");
  const [vista, setVista] = useState(() => {
    if (inicial === "bandeja" && puedeGestionar) return "bandeja";
    return inicial === "mis" ? "mis" : "pedir";
  });
  const [config, setConfig] = useState(null);
  const [recargar, setRecargar] = useState(0);
  const [porRevisar, setPorRevisar] = useState(0);

  useEffect(() => {
    post("/api/pedidomatricula_config", {}).then(({ ok, data }) =>
      setConfig(
        ok ? data : { errorcentral: data.Error || "No se pudo leer la configuración", tipos: [] }
      )
    );
  }, [post]);

  const contar = useCallback(async () => {
    if (!puedeGestionar) return;
    const { ok, data } = await post("/api/pedidomatricula_resumen", {});
    if (ok) setPorRevisar(data.porrevisar || 0);
  }, [post, puedeGestionar]);

  useEffect(() => {
    contar();
  }, [contar]);

  const vistas = VISTAS.filter((v) => !v.gestion || puedeGestionar);

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Pedidos de Matrícula"
        subtitle="Las sucursales piden a la central los documentos de los chasis que vendieron"
        cta={
          config?.central ? (
            <Chip
              label={`Central: ${config.central}`}
              sx={{
                fontWeight: 700,
                color: FP.color.navy,
                bgcolor: FP.color.accentBlueSoft,
                border: `1px solid ${FP.color.border}`,
              }}
            />
          ) : null
        }
      />
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {!config && <CircularProgress size={24} />}
        {config?.errorcentral && <Alert severity="warning">{config.errorcentral}</Alert>}
        {config && !config.errorcentral && (
          <>
            <Box
              sx={{
                display: "flex",
                gap: 0.5,
                p: 0.6,
                mb: 2.6,
                bgcolor: FP.color.cardBg,
                border: `1px solid ${FP.color.border}`,
                borderRadius: 2.5,
                boxShadow: FP.shadow.soft,
                overflowX: "auto",
              }}
            >
              {vistas.map((v) => {
                const Icon = v.icon;
                const activa = vista === v.id;
                const badge = v.id === "bandeja" && porRevisar > 0 ? porRevisar : null;
                return (
                  <Box
                    key={v.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => setVista(v.id)}
                    onKeyDown={(e) => e.key === "Enter" && setVista(v.id)}
                    sx={{
                      flex: { xs: "0 0 auto", md: 1 },
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 0.9,
                      px: 1.8,
                      py: 1.1,
                      borderRadius: 1.8,
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                      userSelect: "none",
                      fontWeight: 700,
                      fontSize: "0.86rem",
                      color: activa ? "#FFFFFF" : FP.color.textSecondary,
                      bgcolor: activa ? FP.color.navy : "transparent",
                      "&:hover": { bgcolor: activa ? FP.color.navySoft : FP.color.surface },
                    }}
                  >
                    <Icon sx={{ fontSize: 18 }} />
                    {v.title}
                    {badge != null && (
                      <Box
                        component="span"
                        sx={{
                          minWidth: 20,
                          height: 20,
                          px: 0.7,
                          borderRadius: 10,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "0.72rem",
                          fontWeight: 800,
                          bgcolor: activa ? "#FFFFFF" : "#E65100",
                          color: activa ? FP.color.navy : "#FFFFFF",
                        }}
                      >
                        {badge}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
            {vista === "pedir" && (
              <Pedir tipos={config.tipos || []} onCreado={() => setRecargar((k) => k + 1)} />
            )}
            {vista === "mis" && <MisPedidos recargar={recargar} />}
            {vista === "bandeja" && puedeGestionar && <Bandeja onCambio={contar} />}
          </>
        )}
      </Box>
    </Box>
  );
}
